import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";

import { ApiError } from "@/modules/shared/utils/ApiError";
import {
  DEFAULT_ENTRY_POINT,
  EntryPoint,
} from "@/modules/tracking/consts/EntryPoints";
import {
  API_ERROR_TYPES,
  ApiErrorType,
  BOOKING_EVENTS,
  RESCHEDULE_EVENTS,
} from "@/modules/tracking/consts/Events";
import { ShortUrlTerminalState } from "@/modules/tracking/consts/ShortUrl";
import { completeShortUrlFlow } from "@/modules/tracking/utils/completeShortUrlFlow";
import { getApiErrorType } from "@/modules/tracking/utils/getApiErrorType";
import {
  registerSuperProperties,
  unregisterSuperProperty,
} from "@/modules/tracking/utils/registerSuperProperties";
import { trackBookingEvent } from "@/modules/tracking/utils/trackBookingEvent";

import { useCoachingApi } from "../../shared/hooks/useCoachingApi";
import { getCoachMatchingAvailabilityKeys } from "../../shared/queries/getCoachMatchingAvailabilityQuery";
import { CreateCoachMatchingSessionResponse } from "../../shared/types/CreateCoachMatchingSessionResponse";
import { extractSessionId } from "../../shared/utils/extractSessionId";
import { generateAvailabilityInterval } from "../helpers/generateAvailabilityInterval";
import { getSessionEndAt } from "../helpers/getSessionEndAt";
import { useBookingSessionStorage } from "../hooks/useBookingSessionStorage";
import { TimezoneOption } from "../types/TimezoneOption";

type AvailabilityInterval = {
  start: string;
  end: string;
};

export const WidgetSteps = {
  SELECT_DATE: "selectDate",
  SELECT_TIME: "selectTime",
  MESSAGE_COACH: "messageCoach",
  SHOW_SUMMARY: "showSummary",
} as const;

type WidgetStep = (typeof WidgetSteps)[keyof typeof WidgetSteps];

export const CreateSessionStatuses = {
  LOADING: "loading",
  ERROR: "error",
  SUCCESS: "success",
} as const;

type CreateSessionStatus =
  (typeof CreateSessionStatuses)[keyof typeof CreateSessionStatuses];

export const CreateSessionErrorReasons = {
  GENERIC: "generic",
  SESSION_ALREADY_STARTED: "sessionAlreadyStarted",
  UNRECOGNIZED_EMAIL: "unrecognizedEmail",
  NO_COACH_AVAILABLE: "noCoachAvailable",
  RATE_LIMITED: "rateLimited",
} as const;

type CreateSessionErrorReason =
  (typeof CreateSessionErrorReasons)[keyof typeof CreateSessionErrorReasons];

export const BookingKinds = {
  COACH_SESSION: "coachSession",
  COACH_MATCHING_SERVICE: "coachMatchingService",
} as const;

export type BookingKind = (typeof BookingKinds)[keyof typeof BookingKinds];

export const COACH_MATCHING_SERVICE_NAME = "Coach Matching Service";

type HandleBookingWidgetCta = ({
  email,
  name,
}: {
  email: string;
  name: string;
}) => Promise<void>;

type MemberPrefill = {
  memberName?: string;
  memberEmail?: string;
  memberPhone?: string;
};

// The Confirm Details draft lives here, not in the panel: BookingWidget
// unmounts the panel on back-navigation, which would drop the member's edits.
type ContactDraft = {
  name?: string;
  email?: string;
};

type TrialInfo = {
  isTrial?: boolean;
  trialEndDate?: string;
};

export type BookingContextType = {
  coachId?: number;
  coachName?: string;
  timezone?: TimezoneOption;
  setCoachData: (data: { id?: number; name?: string }) => void;
  setTimezoneOption: (option: TimezoneOption) => void;
  availabilityInterval: AvailabilityInterval;
  handleAvailabilityIntervalChange: (startDate: Date) => void;
  handleDateSelect: (date: Date | undefined) => void;
  selectedDate?: string;
  selectedDateTime?: string;
  setSelectedDateTime: (dateTime: string | undefined) => void;
  clearSelectedDateTime: () => void;
  clearSelectedDate: () => void;
  handleBookingWidgetCta: HandleBookingWidgetCta;
  createSessionStatus?: CreateSessionStatus;
  createSessionErrorReason?: CreateSessionErrorReason;
  clearCreateSessionErrorReason: () => void;
  returnToTimeSelection: () => void;
  customerId?: number;
  setCustomerId: (id: number) => void;
  currentStep?: WidgetStep;
  memberEmail?: string;
  memberName?: string;
  memberPhone?: string;
  setMemberPrefill: (prefill: MemberPrefill) => void;
  isKnownMember: boolean;
  isPrefillEditable: boolean;
  contactDraft: ContactDraft;
  setContactDraft: (draft: ContactDraft) => void;
  cleanBookingState: () => void;
  isRescheduling: boolean;
  rescheduleSessionId?: number;
  setRescheduleSessionId: (id: number) => void;
  originalSessionStart?: string;
  entryPoint: EntryPoint;
  completedSessionData?: CompletedSessionData;
  isTrial?: boolean;
  trialEndDate?: string;
  setTrialInfo: (info: TrialInfo) => void;
  bookingKind: BookingKind;
  serviceName?: string;
  bookedByUserId?: number;
  setBookedByUserId: (id: number | undefined) => void;
  preSessionNotesEnabled: boolean;
  setPreSessionNotesEnabled: (enabled: boolean) => void;
  multiSessionBookingEnabled: boolean;
  setMultiSessionBookingEnabled: (enabled: boolean) => void;
  preparationNotes: string;
  setPreparationNotes: (notes: string) => void;
  notesStepDone: boolean;
  setNotesStepDone: (done: boolean) => void;
};

export type CompletedSessionData = {
  sessionId?: number;
  sessionStartTime?: string;
};

type ProviderState<T> = T | undefined;

const BookingContext = createContext<BookingContextType | undefined>(undefined);

export function BookingContextFixtureProvider({
  children,
  value,
}: {
  children: ReactNode;
  value: Partial<BookingContextType>;
}) {
  // Development fixtures provide only the fields their children consume.
  return (
    <BookingContext.Provider value={value as BookingContextType}>
      {children}
    </BookingContext.Provider>
  );
}

const isUnrecognizedEmailCreateError = (
  err: unknown,
  attemptUsedMemberEmail: boolean,
  isRescheduling: boolean,
) => {
  if (
    !(err instanceof ApiError) ||
    err.status !== 422 ||
    !attemptUsedMemberEmail ||
    isRescheduling
  ) {
    return false;
  }

  const responseBody = err.responseBody;
  if (
    typeof responseBody !== "object" ||
    responseBody === null ||
    !("error" in responseBody)
  ) {
    return false;
  }

  const responseError = responseBody.error;
  if (
    typeof responseError !== "object" ||
    responseError === null ||
    !("details" in responseError)
  ) {
    return false;
  }

  const details = responseError.details;
  return (
    typeof details === "object" && details !== null && "memberEmail" in details
  );
};

// Glepnir's 503 text when every coach for the slot was taken in the meantime;
// any other 503 (deploy, gateway, maintenance) stays a generic failure.
const NO_COACH_AVAILABLE_MESSAGE =
  "No coaches are available for the requested time slot.";

const getCreateSessionErrorReason = (
  err: unknown,
  isRescheduling: boolean,
  attemptUsedMemberEmail: boolean,
  canReportNoCoachAvailable: boolean,
): CreateSessionErrorReason => {
  if (err instanceof ApiError && err.status === 429) {
    return CreateSessionErrorReasons.RATE_LIMITED;
  }

  if (
    isUnrecognizedEmailCreateError(err, attemptUsedMemberEmail, isRescheduling)
  ) {
    return CreateSessionErrorReasons.UNRECOGNIZED_EMAIL;
  }

  if (
    canReportNoCoachAvailable &&
    err instanceof ApiError &&
    err.status === 503 &&
    err.message === NO_COACH_AVAILABLE_MESSAGE
  ) {
    return CreateSessionErrorReasons.NO_COACH_AVAILABLE;
  }

  if (isRescheduling && err instanceof ApiError && err.status === 422) {
    return CreateSessionErrorReasons.SESSION_ALREADY_STARTED;
  }

  return CreateSessionErrorReasons.GENERIC;
};

const BOOKING_FAILED_ERROR_TYPES: Partial<
  Record<CreateSessionErrorReason, ApiErrorType>
> = {
  [CreateSessionErrorReasons.UNRECOGNIZED_EMAIL]:
    API_ERROR_TYPES.UNRECOGNIZED_EMAIL,
  [CreateSessionErrorReasons.NO_COACH_AVAILABLE]:
    API_ERROR_TYPES.NO_COACH_AVAILABLE,
};

export function BookingContextProvider({
  children,
  overrides,
  initialData,
}: {
  children: ReactNode;
  overrides?: Partial<BookingContextType>;
  initialData?: {
    coachId?: number;
    coachName?: string;
    customerId?: number;
    memberName?: string;
    memberEmail?: string;
    memberPhone?: string;
    isPrefillEditable?: boolean;
    rescheduleSessionId?: number;
    originalSessionStart?: string;
    entryPoint?: EntryPoint;
    isTrial?: boolean;
    trialEndDate?: string;
    bookingKind?: BookingKind;
    serviceName?: string;
    bookedByUserId?: number;
    preSessionNotesEnabled?: boolean;
    multiSessionBookingEnabled?: boolean;
  };
}) {
  const queryClient = useQueryClient();
  const { createCoachMatchingSession, createSession, rescheduleSession } =
    useCoachingApi();

  const {
    persistedState,
    persistSelectedDate,
    persistSelectedDateTime,
    persistTimezone,
    persistPreparationNotes,
    persistNotesStepDone,
    clear: clearSessionStorage,
  } = useBookingSessionStorage();

  const [coachId, setCoachId] = useState<ProviderState<number>>(
    initialData?.coachId,
  );
  const [coachName, setCoachName] = useState<ProviderState<string>>(
    initialData?.coachName,
  );
  // selectedDate, selectedDateTime, and timezone mirror to sessionStorage —
  // always update via the named handlers, never the raw set* directly.
  const [selectedDate, setSelectedDate] = useState<ProviderState<string>>(
    () => persistedState.selectedDate,
  );
  const [selectedDateTime, setSelectedDateTime] = useState<
    ProviderState<string>
  >(() => persistedState.selectedDateTime);
  const [timezone, setTimezone] = useState<ProviderState<TimezoneOption>>(
    () => persistedState.timezone,
  );
  const [availabilityInterval, setAvailabilityInterval] =
    useState<AvailabilityInterval>(generateAvailabilityInterval());
  const [customerId, setCustomerId] = useState<ProviderState<number>>(
    initialData?.customerId,
  );
  const [memberEmail, setMemberEmail] = useState<ProviderState<string>>(
    initialData?.memberEmail,
  );
  const [memberName, setMemberName] = useState<ProviderState<string>>(
    initialData?.memberName,
  );
  const [memberPhone, setMemberPhone] = useState<ProviderState<string>>(
    initialData?.memberPhone,
  );
  const [hasHostMemberIdentity, setHasHostMemberIdentity] = useState(
    () =>
      !(initialData?.isPrefillEditable ?? false) &&
      Boolean(initialData?.memberName || initialData?.memberEmail),
  );
  const [contactDraft, setContactDraftState] = useState<ContactDraft>({});
  const [rescheduleSessionId, setRescheduleSessionId] = useState<
    ProviderState<number>
  >(initialData?.rescheduleSessionId);
  const [originalSessionStart] = useState<ProviderState<string>>(
    initialData?.originalSessionStart,
  );
  const [isTrial, setIsTrial] = useState<ProviderState<boolean>>(
    initialData?.isTrial,
  );
  const [trialEndDate, setTrialEndDate] = useState<ProviderState<string>>(
    initialData?.trialEndDate,
  );
  const [bookedByUserId, setBookedByUserId] = useState<ProviderState<number>>(
    initialData?.bookedByUserId,
  );
  const [preSessionNotesEnabled, setPreSessionNotesEnabled] = useState<boolean>(
    initialData?.preSessionNotesEnabled ?? false,
  );
  const [multiSessionBookingEnabled, setMultiSessionBookingEnabled] =
    useState<boolean>(initialData?.multiSessionBookingEnabled ?? false);
  const [preparationNotes, setPreparationNotes] = useState<string>(
    () => persistedState.preparationNotes ?? "",
  );
  const [notesStepDone, setNotesStepDone] = useState<boolean>(
    () => persistedState.notesStepDone ?? false,
  );
  const [createSessionStatus, setCreateSessionStatus] =
    useState<ProviderState<CreateSessionStatus>>();
  const [createSessionErrorReason, setCreateSessionErrorReason] =
    useState<ProviderState<CreateSessionErrorReason>>();
  const [completedSessionData, setCompletedSessionData] =
    useState<ProviderState<CompletedSessionData>>();

  const timezoneValue = timezone?.value;
  const isPrefillEditable = initialData?.isPrefillEditable ?? false;
  const isKnownMember = customerId !== undefined || hasHostMemberIdentity;
  const entryPoint = initialData?.entryPoint ?? DEFAULT_ENTRY_POINT;
  const bookingKind = initialData?.bookingKind ?? BookingKinds.COACH_SESSION;
  const isCoachMatchingServiceBooking =
    bookingKind === BookingKinds.COACH_MATCHING_SERVICE;
  // Inline unrecognized-email recovery only applies when the member typed the
  // email themselves; host-identified attempts keep the terminal failure panel
  // (their email field is locked, so inline retry would be a dead end).
  const canRecoverUnrecognizedEmailInline = !isKnownMember;
  // Limited to the public page: the in-app embed always arrives identified.
  const canReportNoCoachAvailable =
    isCoachMatchingServiceBooking && !isKnownMember;
  const serviceName =
    initialData?.serviceName ??
    (isCoachMatchingServiceBooking ? COACH_MATCHING_SERVICE_NAME : undefined);

  useEffect(() => {
    if (customerId !== undefined) {
      registerSuperProperties({
        entry_point: entryPoint,
        customer_id: customerId,
      });
    } else {
      registerSuperProperties({ entry_point: entryPoint });
      unregisterSuperProperty("customer_id");
    }
  }, [customerId, entryPoint]);

  const handleReschedule = useCallback(async () => {
    if (!selectedDateTime || !timezoneValue || rescheduleSessionId == null) {
      return;
    }

    let responseBody: unknown;
    try {
      responseBody = await rescheduleSession({
        sessionId: rescheduleSessionId,
        startAt: selectedDateTime,
        endAt: getSessionEndAt(selectedDateTime, timezoneValue),
        timezone: timezoneValue,
        summary: "",
        description: "",
      });
    } catch (err) {
      trackBookingEvent(RESCHEDULE_EVENTS.RESCHEDULE_FAILED, {
        coach_id: coachId ?? null,
        original_session_id: rescheduleSessionId,
        error_type: getApiErrorType(err),
      });
      throw err;
    }

    const newSessionId = extractSessionId(responseBody);

    trackBookingEvent(RESCHEDULE_EVENTS.RESCHEDULE_COMPLETED, {
      coach_id: coachId ?? null,
      original_session_id: rescheduleSessionId,
      original_session_start_time: originalSessionStart ?? null,
      session_start_time: selectedDateTime,
      known_member: Boolean(memberName || memberEmail),
      entry_point: entryPoint,
    });

    completeShortUrlFlow(ShortUrlTerminalState.Rescheduled, {
      extraProps: { coach_id: coachId ?? null },
    });

    await queryClient.invalidateQueries({
      queryKey: ["getCoachAvailability"],
    });
    setCompletedSessionData({
      sessionId: newSessionId,
      sessionStartTime: selectedDateTime,
    });
    setCreateSessionStatus("success");
    clearSessionStorage();
  }, [
    clearSessionStorage,
    coachId,
    entryPoint,
    memberEmail,
    memberName,
    originalSessionStart,
    queryClient,
    rescheduleSession,
    rescheduleSessionId,
    selectedDateTime,
    timezoneValue,
  ]);

  const handleBook = useCallback(
    async ({ name, email }: { email: string; name: string }) => {
      if (
        !selectedDateTime ||
        !timezoneValue ||
        (!isCoachMatchingServiceBooking && !coachId) ||
        (!customerId && !email)
      ) {
        return;
      }

      setMemberEmail(email);
      setMemberName(name);

      const endAt = getSessionEndAt(selectedDateTime, timezoneValue);

      // Only the network create is guarded. Success side effects (analytics,
      // cache invalidation, completed state) run after the try/catch so a throw
      // *after* a successful booking can't flip the user to the failed panel.
      let coachMatchingResponse: CreateCoachMatchingSessionResponse | undefined;

      try {
        if (isCoachMatchingServiceBooking) {
          coachMatchingResponse = await createCoachMatchingSession({
            ...(customerId ? { customerId } : { memberEmail: email }),
            startAt: selectedDateTime,
            endAt,
            timezone: timezoneValue,
            ...(preSessionNotesEnabled && preparationNotes
              ? { preparationNotes }
              : {}),
          });
        } else {
          await createSession({
            coachId: coachId as number,
            ...(customerId ? { customerId } : { memberEmail: email }),
            startAt: selectedDateTime,
            endAt,
            timezone: timezoneValue,
            summary: "",
            description: "",
            ...(preSessionNotesEnabled && preparationNotes
              ? { preparationNotes }
              : {}),
            ...(bookedByUserId !== undefined ? { bookedByUserId } : {}),
          });
        }
      } catch (err) {
        const errorReason = getCreateSessionErrorReason(
          err,
          false,
          canRecoverUnrecognizedEmailInline,
          canReportNoCoachAvailable,
        );
        const errorType =
          BOOKING_FAILED_ERROR_TYPES[errorReason] ?? getApiErrorType(err);
        const failureProperties = isCoachMatchingServiceBooking
          ? { service_name: serviceName, error_type: errorType }
          : { coach_id: coachId, error_type: errorType };

        trackBookingEvent(BOOKING_EVENTS.BOOKING_FAILED, failureProperties);

        if (errorReason === CreateSessionErrorReasons.NO_COACH_AVAILABLE) {
          queryClient.invalidateQueries({
            queryKey: getCoachMatchingAvailabilityKeys.root,
          });
        }
        throw err;
      }

      if (isCoachMatchingServiceBooking) {
        const sessionStartTime =
          coachMatchingResponse?.startsAt || selectedDateTime;

        trackBookingEvent(BOOKING_EVENTS.BOOKING_COMPLETED, {
          service_name: serviceName,
          session_id: coachMatchingResponse?.sessionId,
          session_start_time: sessionStartTime,
          known_member: Boolean(name || email),
          entry_point: entryPoint,
        });

        await queryClient.invalidateQueries({
          queryKey: getCoachMatchingAvailabilityKeys.root,
        });
        setCompletedSessionData({
          sessionId: coachMatchingResponse?.sessionId,
          sessionStartTime,
        });
        setCreateSessionStatus("success");
        clearSessionStorage();
        return;
      }

      trackBookingEvent(BOOKING_EVENTS.BOOKING_COMPLETED, {
        coach_id: coachId,
        session_start_time: selectedDateTime,
        known_member: Boolean(name || email),
        entry_point: entryPoint,
      });

      completeShortUrlFlow(ShortUrlTerminalState.BookingCompleted, {
        extraProps: { coach_id: coachId },
      });

      await queryClient.invalidateQueries({
        queryKey: ["getCoachAvailability"],
      });
      setCompletedSessionData({ sessionStartTime: selectedDateTime });
      setCreateSessionStatus("success");
      clearSessionStorage();
    },
    [
      bookedByUserId,
      canRecoverUnrecognizedEmailInline,
      canReportNoCoachAvailable,
      clearSessionStorage,
      coachId,
      createCoachMatchingSession,
      createSession,
      customerId,
      entryPoint,
      isCoachMatchingServiceBooking,
      preSessionNotesEnabled,
      preparationNotes,
      queryClient,
      selectedDateTime,
      serviceName,
      timezoneValue,
    ],
  );

  const handleBookingWidgetCta = useCallback(
    async ({ name, email }: { email: string; name: string }) => {
      try {
        setCreateSessionErrorReason(undefined);
        setCreateSessionStatus("loading");

        if (rescheduleSessionId != null) {
          await handleReschedule();
          return;
        }

        await handleBook({ name, email });
      } catch (err) {
        const isRescheduling = rescheduleSessionId != null;
        const errorReason = getCreateSessionErrorReason(
          err,
          isRescheduling,
          canRecoverUnrecognizedEmailInline,
          canReportNoCoachAvailable,
        );
        const isRetryableInline =
          errorReason === CreateSessionErrorReasons.UNRECOGNIZED_EMAIL ||
          errorReason === CreateSessionErrorReasons.RATE_LIMITED;
        setCreateSessionErrorReason(errorReason);
        setCreateSessionStatus(isRetryableInline ? undefined : "error");
      }
    },
    [
      canRecoverUnrecognizedEmailInline,
      canReportNoCoachAvailable,
      handleBook,
      handleReschedule,
      rescheduleSessionId,
    ],
  );

  const clearCreateSessionErrorReason = useCallback(() => {
    setCreateSessionErrorReason(undefined);
  }, []);

  const setCoachData = useCallback(
    ({ id, name }: { id?: number; name?: string }) => {
      if (id) {
        setCoachId(id);
      }
      if (name) {
        setCoachName(name);
      }
    },
    [],
  );

  // Does NOT protect against overwriting user edits — that's handled by
  // ConfirmDetailsPanel via the hasUserEdited refs.
  const setMemberPrefill = useCallback(
    (prefill: MemberPrefill) => {
      if (prefill.memberName) {
        setMemberName(prefill.memberName);
      }
      if (prefill.memberEmail) {
        setMemberEmail(prefill.memberEmail);
      }
      if (prefill.memberPhone) {
        setMemberPhone(prefill.memberPhone);
      }
      if (
        !isPrefillEditable &&
        Boolean(prefill.memberName || prefill.memberEmail)
      ) {
        setHasHostMemberIdentity(true);
      }
    },
    [isPrefillEditable],
  );

  const setContactDraft = useCallback((draft: ContactDraft) => {
    setContactDraftState((current) => ({ ...current, ...draft }));
  }, []);

  const setTrialInfo = useCallback((info: TrialInfo) => {
    setIsTrial(info.isTrial);
    setTrialEndDate(info.trialEndDate);
  }, []);

  const setTimezoneOption = useCallback(
    (option: TimezoneOption) => {
      setTimezone(option);
      persistTimezone(option);
    },
    [persistTimezone],
  );

  const handleAvailabilityIntervalChange = useCallback((startDate: Date) => {
    const newInterval = generateAvailabilityInterval(startDate);
    setAvailabilityInterval(newInterval);
  }, []);

  const handleDateSelect = useCallback(
    (date?: Date) => {
      if (!date) {
        setSelectedDate(undefined);
        persistSelectedDate(undefined);
        return;
      }

      const dateWithTimeZone = format(date, "yyyy-MM-dd");

      setSelectedDate(dateWithTimeZone);
      persistSelectedDate(dateWithTimeZone);
    },
    [persistSelectedDate],
  );

  const handleSetSelectedDateTime = useCallback(
    (dateTime: string | undefined) => {
      setSelectedDateTime(dateTime);
      persistSelectedDateTime(dateTime);
    },
    [persistSelectedDateTime],
  );

  const clearSelectedDateTime = useCallback(() => {
    setSelectedDateTime(undefined);
    persistSelectedDateTime(undefined);
    setCreateSessionErrorReason(undefined);
  }, [persistSelectedDateTime]);

  const returnToTimeSelection = useCallback(() => {
    setCreateSessionStatus(undefined);
    clearSelectedDateTime();
  }, [clearSelectedDateTime]);

  const clearSelectedDate = useCallback(() => {
    setSelectedDate(undefined);
    persistSelectedDate(undefined);
  }, [persistSelectedDate]);

  const handleSetPreparationNotes = useCallback(
    (notes: string) => {
      setPreparationNotes(notes);
      persistPreparationNotes(notes);
    },
    [persistPreparationNotes],
  );

  const handleSetNotesStepDone = useCallback(
    (done: boolean) => {
      setNotesStepDone(done);
      persistNotesStepDone(done);
    },
    [persistNotesStepDone],
  );

  const isRescheduling = rescheduleSessionId != null;

  const currentStep = useMemo<WidgetStep>(() => {
    if (!selectedDate && !selectedDateTime) {
      return WidgetSteps.SELECT_DATE;
    }

    if (selectedDate && !selectedDateTime) {
      return WidgetSteps.SELECT_TIME;
    }

    if (preSessionNotesEnabled && !isRescheduling && !notesStepDone) {
      return WidgetSteps.MESSAGE_COACH;
    }

    return WidgetSteps.SHOW_SUMMARY;
  }, [
    selectedDate,
    selectedDateTime,
    preSessionNotesEnabled,
    isRescheduling,
    notesStepDone,
  ]);

  // Member prefill (name / email / phone) and trial info (isTrial /
  // trialEndDate) are retained across resets: the host injects both only on
  // load, so wiping them would re-enable booking past the trial limit.
  const cleanBookingState = useCallback(() => {
    setAvailabilityInterval(generateAvailabilityInterval());
    setSelectedDate(undefined);
    setSelectedDateTime(undefined);
    setTimezone(undefined);
    setPreparationNotes("");
    setNotesStepDone(false);
    setCreateSessionStatus(undefined);
    setCreateSessionErrorReason(undefined);
    setCompletedSessionData(undefined);
    clearSessionStorage();
  }, [clearSessionStorage]);

  const value = useMemo(
    () => ({
      availabilityInterval,
      clearSelectedDate,
      clearSelectedDateTime,
      coachId,
      coachName,
      handleAvailabilityIntervalChange,
      handleBookingWidgetCta,
      handleDateSelect,
      selectedDate,
      selectedDateTime,
      setCoachData,
      setCustomerId,
      setSelectedDateTime: handleSetSelectedDateTime,
      setTimezoneOption,
      timezone,
      currentStep,
      createSessionStatus,
      createSessionErrorReason,
      clearCreateSessionErrorReason,
      returnToTimeSelection,
      customerId,
      memberEmail,
      memberName,
      memberPhone,
      setMemberPrefill,
      isKnownMember,
      isPrefillEditable,
      contactDraft,
      setContactDraft,
      cleanBookingState,
      isRescheduling,
      rescheduleSessionId,
      setRescheduleSessionId,
      originalSessionStart,
      entryPoint,
      completedSessionData,
      isTrial,
      trialEndDate,
      setTrialInfo,
      bookingKind,
      serviceName,
      bookedByUserId,
      setBookedByUserId,
      preSessionNotesEnabled,
      setPreSessionNotesEnabled,
      multiSessionBookingEnabled,
      setMultiSessionBookingEnabled,
      preparationNotes,
      setPreparationNotes: handleSetPreparationNotes,
      notesStepDone,
      setNotesStepDone: handleSetNotesStepDone,
    }),
    [
      availabilityInterval,
      clearSelectedDate,
      clearSelectedDateTime,
      coachId,
      coachName,
      handleAvailabilityIntervalChange,
      handleBookingWidgetCta,
      handleDateSelect,
      selectedDate,
      selectedDateTime,
      setCoachData,
      handleSetSelectedDateTime,
      setTimezoneOption,
      timezone,
      currentStep,
      createSessionStatus,
      createSessionErrorReason,
      clearCreateSessionErrorReason,
      returnToTimeSelection,
      customerId,
      memberEmail,
      memberName,
      memberPhone,
      setMemberPrefill,
      isKnownMember,
      isPrefillEditable,
      contactDraft,
      setContactDraft,
      cleanBookingState,
      isRescheduling,
      rescheduleSessionId,
      setRescheduleSessionId,
      originalSessionStart,
      entryPoint,
      completedSessionData,
      isTrial,
      trialEndDate,
      setTrialInfo,
      bookingKind,
      serviceName,
      bookedByUserId,
      setBookedByUserId,
      preSessionNotesEnabled,
      multiSessionBookingEnabled,
      preparationNotes,
      handleSetPreparationNotes,
      notesStepDone,
      handleSetNotesStepDone,
    ],
  );

  return (
    <BookingContext.Provider value={{ ...value, ...overrides }}>
      {children}
    </BookingContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useBookingContext() {
  const context = useContext(BookingContext);

  if (!context) {
    throw new Error(
      "useBookingContext must be used within a BookingContextProvider",
    );
  }

  return context;
}
